import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import withIntl from '../lib/withIntl';

const StepperContainer = styled.div`
  margin: 0 auto;
  white-space: nowrap;
`;

const StepLine = styled.line.attrs((props) => ({
  y1: props.isResponsive ? '15%' : '25',
  y2: props.isResponsive ? '15%' : '25',
  ...props,
}))`
  stroke: ${(prop) => prop.stroke};
  stroke-width: ${(prop) => (prop.isResponsive ? '.3vw' : '4')};
`;

function StepCircleWithNumber({x, stepNumber, color, handleClickStep, isResponsive, isAllowedToClick}) {
  const cy = isResponsive ? '1vw' : '25';
  const r = isResponsive ? '1vw' : 18;

  const y = isResponsive ? '16%' : '25';
  const fontSize = isResponsive ? '1.5vw' : 24;

  return (
    <g
      key={stepNumber}
      onClick={() => handleClickStep(stepNumber)}
      style={{cursor: isAllowedToClick ? 'pointer' : 'default'}}
    >
      <circle cx={x} cy={cy} r={r} fill={color} />
      <text
        x={x}
        y={y}
        textAnchor="middle"
        alignmentBaseline="central"
        fill="#FFFFFF"
        style={{fontSize, fontWeight: 700}}
      >
        {stepNumber}
      </text>
    </g>
  );
}

StepCircleWithNumber.propTypes = {
  x: PropTypes.string.isRequired,
  stepNumber: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  handleClickStep: PropTypes.func.isRequired,
  isResponsive: PropTypes.bool.isRequired,
  isAllowedToClick: PropTypes.bool.isRequired,
};

function Stepper({
  currentWizardView,
  onClickStep,
  isAllowedToClick,
  stepsLabel,
  height,
  width,
  offset,
  wrapLabel,
  isResponsive,
  skippedStep,
  processedStep,
}) {
  const numberOfLabels = stepsLabel.length;

  function handleClickStep(targetStep) {
    if (!isAllowedToClick) {
      return;
    }
    onClickStep(targetStep);
  }

  function calculateCircleColor(circleNumber) {
    if (currentWizardView === circleNumber) {
      return '#F7A246';
    }

    if (skippedStep.includes(circleNumber)) {
      return '#969696';
    }

    if (currentWizardView > circleNumber || processedStep?.includes(circleNumber)) {
      return '#37C871';
    }

    return '#969696';
  }

  function calculateLineColor(lineTargetNumber) {
    if (currentWizardView === 1) return '#969696';

    if (skippedStep.includes(lineTargetNumber)) {
      return '#969696';
    }

    if (
      (currentWizardView > lineTargetNumber && lineTargetNumber !== -1) ||
      processedStep?.includes(lineTargetNumber)
    ) {
      return '#37C871';
    }

    return '#969696';
  }

  const stepperLabel = (stepLabel) => {
    const y = isResponsive ? '3vw' : 45.5;
    // eslint-disable-next-line react/destructuring-assignment
    const whiteSpace = stepLabel.length > 18 || wrapLabel ? 'break-spaces' : 'none';
    const fontSize = isResponsive ? '1vw' : 14;

    return (
      <foreignObject x="0" y={y} width="100%" height="100%">
        <div style={{whiteSpace, fontSize, fontStyle: 'italic', color: '#909090'}}>{stepLabel}</div>
      </foreignObject>
    );
  };

  function generateStepper() {
    const stepperComponent = [];
    let widthNumber = width;
    let widthSuffix = '';
    let offsetWidth = 5;

    if (typeof width === 'string') {
      widthNumber = parseFloat(width.replace(/\D/g, ''));
      widthSuffix = width.replace(/[0-9]/g, '');
      offsetWidth = 0;
    }

    if (widthSuffix === '' && isResponsive) {
      widthSuffix = 'vw';
      offsetWidth = 0;
    }

    const middleWidth = Math.floor(widthNumber / numberOfLabels) + offsetWidth;
    const edgeWidth = Math.abs((widthNumber - (numberOfLabels - 2) * middleWidth) / 2);

    let gapLength = 75 / (numberOfLabels - 1);

    if (offset > 0) gapLength = (90 - offset) / (numberOfLabels - 1);

    for (let index = 0; index < numberOfLabels; index += 1) {
      const stepNumber = index + 1;
      const stepPosition = gapLength * index;
      const stepLabel = stepsLabel[index];

      const offsetX = offset > 0 ? `${50 + offset}%` : '50%'; // default
      const stepLineHidden = {x1: 0, x2: 0};
      let stepLinesForward = {x1: offsetX, x2: '100%'};
      let stepLinesBackward = {x1: '0%', x2: offsetX};

      if (stepNumber === 1) {
        stepLinesBackward = stepLineHidden;
      } else if (stepNumber === numberOfLabels) {
        stepLinesForward = stepLineHidden;
      }

      stepperComponent.push(
        <svg
          height="100%"
          key={stepNumber}
          x={`${stepPosition}%`}
          width={
            stepNumber === 1 || stepNumber === numberOfLabels
              ? `${edgeWidth}${widthSuffix}`
              : `${middleWidth}${widthSuffix}`
          }
        >
          <StepLine
            isResponsive={isResponsive}
            stepNumber={stepNumber - 1}
            stroke={calculateLineColor(stepNumber - 1)}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...stepLinesBackward}
          />
          <StepLine
            isResponsive={isResponsive}
            stepNumber={stepNumber}
            stroke={calculateLineColor(stepNumber)}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...stepLinesForward}
          />
          <StepCircleWithNumber
            x={offsetX}
            stepNumber={stepNumber}
            color={calculateCircleColor(stepNumber)}
            handleClickStep={handleClickStep}
            isResponsive={isResponsive}
            isAllowedToClick={isAllowedToClick}
          />
          {stepperLabel(stepLabel)}
        </svg>,
      );
    }

    return stepperComponent;
  }

  return <StepperContainer style={{height, width}}>{generateStepper()}</StepperContainer>;
}

export default withIntl(Stepper);

Stepper.propTypes = {
  currentWizardView: PropTypes.number.isRequired,
  onClickStep: PropTypes.func,
  isAllowedToClick: PropTypes.bool,
  stepsLabel: PropTypes.array.isRequired,
  height: PropTypes.any,
  width: PropTypes.any,
  offset: PropTypes.number,
  wrapLabel: PropTypes.bool.isRequired,
  isResponsive: PropTypes.bool,
  skippedStep: PropTypes.array,
  processedStep: PropTypes.array,
};

Stepper.defaultProps = {
  onClickStep: () => {},
  offset: 0,
  width: 1000,
  height: '',
  isAllowedToClick: false,
  isResponsive: false,
  skippedStep: [],
  processedStep: [],
};
